import { Outlet, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SOCIAL_PUBLISHING_ENABLED } from '../config/socialPublishing';
import { RecoveryCodesNudge } from './security/RecoveryCodesNudge';
import {
  Package,
  Layers,
  Rocket,
  Shield,
  ShieldCheck,
  Users,
  CircleUser,
  Menu,
  X,
  Activity,
  Cpu,
  BarChart3,
  UserPlus,
  GraduationCap,
  HeadphonesIcon,
  Bug,
  Command,
  ChevronDown,
  ChevronRight,
  BadgeDollarSign,
  GitBranch,
  LayoutGrid,
  Monitor,
  DollarSign,
  ScrollText,
  Tags,
  FileSignature,
  PenTool,
  Boxes,
  FolderSearch,
  ShoppingCart,
  Truck,
  ClipboardList,
  FileCheck2,
  PackagePlus,
  Building2,
  SlidersHorizontal,
  ShieldAlert,
  Wrench,
  Link2,
  Tag, Percent, CreditCard, ArrowLeftRight, KeyRound, Smartphone, History, Receipt, DatabaseZap, Share2, LayoutTemplate, Fingerprint } from 'lucide-react';
import { isDevHost } from '../config/devOnly';
import { EnvBanner } from './EnvBanner';
import { AccountMenu } from './AccountMenu';
import { getAdminProductConfig } from '../config/productConfig';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStaffAccess } from '../hooks/useStaffAccess';
import { isLegacyFleetUrl } from '../pages/fleet/fleetAccess';
import { ViewOnlyBanner } from './security/ViewOnlyBanner';
import { RubyLoader } from './RubyLoader';
import IssueReporterRoot from './issueReporter/IssueReporterRoot';
import { AppMenuRouteGuard, readBuildAppMenu, isRouteAllowed } from '@platform/mobile-shell';
import { openExternal, webOrigin } from '../mobile/shell';

// RubyTill Admin phone builds: routes switched on in Mobile Apps ▸ Apps & menus (null on the web).
const APP_MENU = readBuildAppMenu();

// Build the issue-reporter user from the stored Cognito ID token. Super admins
// aren't tenant-scoped, so reports are tagged with a 'super-admin' sentinel
// tenant on the backend (createIssue falls back to context.tenantId).
function buildIssueUser(fallbackEmail?: string) {
  try {
    const token = localStorage.getItem('jwtToken') || '';
    const parts = token.split('.');
    if (parts.length === 3) {
      const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const claimEmail = claims.email || fallbackEmail || '';
      return {
        id: claims.sub || claimEmail || 'super-admin',
        email: claimEmail,
        role: claims['custom:role'] || 'super_admin',
        name: claims.name || (claimEmail ? claimEmail.split('@')[0] : 'Super Admin'),
      };
    }
  } catch { /* fall through to email-only */ }
  return fallbackEmail
    ? { id: fallbackEmail, email: fallbackEmail, role: 'super_admin', name: fallbackEmail.split('@')[0] }
    : null;
}

interface NavItem {
  path: string;
  label: string;
  icon: any;
  /** Match this path exactly, so a parent doesn't stay highlighted on its child routes. */
  end?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { path: '/command-center', label: 'Command Center', icon: Command },
      { path: '/finance', label: 'Finance Hub', icon: DollarSign },
    ],
  },
  {
    title: 'AI & Analytics',
    items: [
      // Was two entries — /ai-status ("AI Status") and /ai-usage ("Agent
      // Monitor"). One subject, one page (spec §6). Both paths still redirect.
      { path: '/ai', label: 'AI Control Center', icon: Cpu },
      { path: '/analytics', label: 'Website Analytics', icon: BarChart3 },
    ],
  },
  {
    // "Partners" covers both kinds: referral partners, and the internal ISOs
    // who work merchant-processing orders. Same records, same portal — the
    // `isInternalIso` flag is what separates them. Route stays /affiliates so
    // no bookmark, deep link or test breaks over a word.
    title: 'Partners',
    items: [
      { path: '/affiliates', label: 'All partners', icon: UserPlus, end: true },
      { path: '/affiliates/certification', label: 'Certification', icon: GraduationCap },
      // The ONE place partner commission rates are edited. A deal points at a
      // plan; nothing carries its own rates any more.
      { path: '/commission-plans', label: 'Commission Plans', icon: Percent },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { path: '/menus', label: 'Menus', icon: LayoutGrid },
      { path: '/packages', label: 'Packages', icon: Package },
      { path: '/products', label: 'Products', icon: Layers },
      // Integrations › Social publishing — RubyTill's own Google/Meta app keys (section /integrations).
      ...(SOCIAL_PUBLISHING_ENABLED ? [{ path: '/integrations/social', label: 'Social publishing', icon: Share2 }] : []),
    ],
  },
  {
    // Internal Tools — operational tools super admins switch on per tenant.
    // Never sold: no Stripe item, not in packages or merchant billing.
    title: 'Tools',
    items: [
      { path: '/tools', label: 'Tool Library', icon: Wrench, end: true },
      { path: '/tools/activity', label: 'Tool Activity', icon: History },
      { path: '/tools/data-migration', label: 'Data Migration', icon: DatabaseZap },
    ],
  },
  {
    // Everything about getting a release onto a device: the deploy pipeline
    // dashboard, the release hub (versions/builds/rollout — Phase D), and
    // per-device fleet/branding management. Grouped so ops can find the
    // whole "ship it" story in one place.
    title: 'Deployment & Release',
    items: [
      { path: '/deployments', label: 'Deployments', icon: GitBranch },
      // The release hub: Apps / Builds / Rollout tabs. Rollout links into the
      // ONE fleet table on /pos-config; it never lists devices itself.
      { path: '/releases', label: 'Releases', icon: Rocket },
      { path: '/mobile-apps', label: 'Mobile Apps', icon: Smartphone },
      // Provisioning sits with the release story, not with Inventory: it is
      // the per-device checklist written against the ORDER's stages, worked
      // on the bench and on the order — the same "ship it" story as the two
      // above. Under Inventory it read as a stock function, which is the one
      // thing it is not. The route is unchanged.
      { path: '/inventory/provisioning', label: 'Provisioning', icon: Wrench },
      // end: Payment processors is a /pos-config child with its own menu item — only one lights up.
      { path: '/pos-config', label: 'Device Management', icon: Monitor, end: true },
      // Platform processor credentials (MagTek UniGate) — a /pos-config child, same permission.
      { path: '/pos-config/payment-processors', label: 'Payment processors', icon: KeyRound },
      // The GLOBAL RubyTill till layout + receipt every business starts on.
      { path: '/pos-defaults', label: 'POS Defaults', icon: LayoutTemplate },
      { path: '/fleet-monitoring', label: 'Fleet Monitoring', icon: Activity },
    ],
  },
  {
    title: 'Inventory',
    items: [
      // The old "Inventory Hub" merged-tab entry is retired — each of its
      // tabs is its own side-nav page now (InventoryHubPage renders one
      // section per route; legacy /inventory/catalog|procurement redirect).
      // MERGED 2026-08-30: Stock and Financing are one screen. The Financing
      // entry is gone rather than pointed at the same page — two menu items
      // landing in the same place is how a merge reads as a bug.
      { path: '/inventory/stock', label: 'Devices', icon: Monitor },
      { path: '/inventory/finance-book', label: 'Finance Book', icon: CreditCard },
      { path: '/inventory/receiving', label: 'Receiving', icon: PackagePlus },
      { path: '/inventory/vendors', label: 'Vendors', icon: Building2 },
      { path: '/inventory/adjustments', label: 'Adjustments', icon: SlidersHorizontal },
      { path: '/inventory/damage', label: 'Damage', icon: ShieldAlert },
      { path: '/inventory/brands', label: 'Brands', icon: Tag },
      { path: '/inventory/agreements', label: 'Agreements', icon: FileCheck2 },
      { path: '/inventory/reports', label: 'Reports', icon: ClipboardList },
    ],
  },
  {
    // Selling surfaces pulled out of Inventory into their own group —
    // Bundles/Deals are route-per-section (SalesPage), Orders unchanged.
    title: 'Sales',
    items: [
      { path: '/inventory/bundles', label: 'Bundles', icon: ShoppingCart },
      { path: '/inventory/deals', label: 'Deals', icon: BadgeDollarSign },
      { path: '/inventory/orders', label: 'Orders', icon: Truck },
      { path: '/business-transfers', label: 'Business transfers', icon: ArrowLeftRight },
    ],
  },
  {
    // Ruby Estate (Task 34) — landlords, fee plans and Stripe settings for the
    // property-management product line. Gated the same way as every other
    // group: `staffAccess.hasAccess('/estate-admin...')` below. Until Task 33
    // lands the `/estate-admin` section in the shared adminRouteMap, only the
    // Owner (who fails open per useStaffAccess) will see this group — see the
    // task report for that dependency.
    title: 'Ruby Estate',
    items: [
      { path: '/estate-admin', label: 'Overview', icon: Building2, end: true },
      { path: '/estate-admin/businesses', label: 'Landlords', icon: Users },
      { path: '/estate-admin/fee-plans', label: 'Fee plans', icon: Percent },
      { path: '/estate-admin/fees', label: 'Fees report', icon: Receipt },
      { path: '/estate-admin/stripe', label: 'Stripe settings', icon: KeyRound },
    ],
  },
  {
    title: 'Legal',
    items: [
      { path: '/legal/policies', label: 'Policies', icon: ScrollText },
      { path: '/legal/agreements', label: 'Agreements', icon: FileSignature },
      { path: '/legal/categories', label: 'Categories', icon: Tags },
      { path: '/legal/company', label: 'Company Profile', icon: Building2 },
      { path: '/legal/packs', label: 'Packs', icon: Boxes },
      { path: '/legal/bindings', label: 'Bindings', icon: Link2 },
      { path: '/legal/hub', label: 'Documents Hub', icon: FolderSearch },
      { path: '/legal/signatures', label: 'Signatures', icon: PenTool },
    ],
  },
  {
    title: 'System',
    items: [
      { path: '/support', label: 'Support Tickets', icon: HeadphonesIcon },
      { path: '/app-issues', label: 'Issue Console', icon: Bug },
      // Approvals + developer access for the Issue Console (dev host only
      // until the support-service routes ship to prod).
      ...(isDevHost() ? [{ path: '/controls', label: 'Controls', icon: KeyRound }] : []),
      { path: '/activity', label: 'Activity Log', icon: Activity },
      // Task 19 — the Stripe key, webhook and consent settings identity checks run on.
      { path: '/identity-admin', label: 'ID Verification', icon: Fingerprint },
    ],
  },
  {
    // RubyTill's OWN employees (staff-service) — hiring, onboarding
    // workflows, roles & panel access. "My Portal" is every member's own
    // onboarding view and is always accessible.
    title: 'Team',
    items: [
      { path: '/staff-portal', label: 'My Portal', icon: CircleUser },
      { path: '/staff', label: 'Team Management', icon: Users, end: true },
      { path: '/staff/roles', label: 'Roles & Access', icon: ShieldCheck },
    ],
  },
];

function Layout() {
  const { email, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const product = getAdminProductConfig();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(navGroups.map(g => g.title));

  // Panel authorization (staff-service section levels). FAILS CLOSED: only
  // the Owner (isOwner from /me) sees everything; everyone else sees exactly
  // what their role's levels grant, and a /me error shows a retry card
  // instead of the menu or any page content (see useStaffAccess.tsx).
  const staffAccess = useStaffAccess();
  const visibleGroups = navGroups
    .map(group => ({ ...group, items: group.items.filter(item => staffAccess.hasAccess(item.path) && isRouteAllowed(item.path, APP_MENU)) }))
    .filter(group => group.items.length > 0);
  // Legacy fleet URLs (deviceHref, Releases → Rollout) also admit a
  // /fleet-monitoring holder, so LegacyFleetGate / LegacyDeviceRoute can
  // redirect them. /pos-config-only users are unaffected (Task 24).
  const routeAllowed = staffAccess.loading || staffAccess.hasAccess(location.pathname)
    || (isLegacyFleetUrl(location.pathname, location.search) && staffAccess.hasAccess('/fleet-monitoring'));

  /*
    THE SHELL NEVER LETS THE DOCUMENT SCROLL. The shell is `h-screen` and the
    page scrolls inside <main>; but anything that makes the document taller
    than the window (a portal, a tooltip positioned off a long list, a
    scrollIntoView) let the wheel CHAIN past the end of <main> into the
    document, and the whole shell slid up leaving the body's dark background
    under it. Locked only while this shell is mounted, so login and any
    full-page route keep ordinary scrolling.
  */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow, html.style.height, body.style.height];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.height = '100%';
    window.scrollTo(0, 0);
    return () => {
      [html.style.overflow, body.style.overflow, html.style.height, body.style.height] = prev;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev =>
      prev.includes(title)
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  return (
    <div className="h-screen bg-stone-100 flex overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-black border-r border-dark-800
          transform transition-transform lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Environment strip — local / dev / staging only; production stays clean. */}
        <EnvBanner />

        {/* Logo — vertical stack: big tile + wordmark + Super Admin caption,
            matching the platform-app sidebar so the brand reads identically
            across both apps. */}
        <div className="relative px-4 pt-5 pb-4 border-b border-dark-800 bg-black flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-3 right-3 text-stone-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center gap-2.5">
            <div className="w-[70px] h-[70px] rounded-2xl bg-neutral-900 ring-2 ring-white/20 shadow-lg flex items-center justify-center overflow-hidden">
              <product.Icon size={46} className="text-red-500" />
            </div>
            <div className="font-extrabold text-lg tracking-tight leading-none">
              {product.brandHeading ?? <span className="text-white">{product.adminTitle}</span>}
            </div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500">Super Admin</p>
          </div>
        </div>

        {/* Navigation - scrollable area */}
        <nav className="flex-1 overflow-y-auto scrollbar-hide p-3 space-y-1">
          {visibleGroups.map((group) => (
            <div key={group.title} className="space-y-0.5">
              <button
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-stone-600 uppercase tracking-wider hover:text-stone-400"
              >
                {group.title}
                {expandedGroups.includes(group.title) ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
              {expandedGroups.includes(group.title) && (
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.end}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                          isActive
                            ? 'bg-admin-primary text-white'
                            : 'text-stone-400 hover:text-white hover:bg-dark-800'
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Account — one row that opens a menu (My portal, copy, sign out). */}
        <AccountMenu
          email={email}
          name={staffAccess.me?.staff?.name}
          role={staffAccess.me?.role?.name || 'Super Admin'}
          onSignOut={handleLogout}
          onNavigate={() => setSidebarOpen(false)}
        />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile menu button - only visible on mobile */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-dark-800 rounded-lg text-stone-400 hover:text-white shadow-lg"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Page content - full height. Staff whose role doesn't grant the
            current section are sent to their own portal (never a lockout —
            /staff-portal is always allowed). */}
        <main className="flex-1 p-3 md:p-6 overflow-auto overscroll-contain">
          {staffAccess.error ? (
            <div className="max-w-md mx-auto mt-16 bg-white border border-stone-200 rounded-xl p-6 text-center">
              <p className="font-semibold text-stone-900">We couldn’t load your access</p>
              <p className="text-sm text-stone-600 mt-1">For safety nothing is shown until your role loads.</p>
              <button onClick={staffAccess.retry} className="mt-4 min-h-[44px] px-4 rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white">Try again</button>
            </div>
          ) : staffAccess.loading ? (
            // No page mounts (so no page API calls fire) until /me resolves.
            <RubyLoader variant="page" />
          ) : routeAllowed ? (
            <AppMenuRouteGuard
              allow={APP_MENU}
              path={location.pathname + location.search}
              webOrigin={webOrigin()}
              routing="path"
              theme="light"
              onBack={() => navigate('/')}
              openUrl={openExternal}
            >
              {location.pathname !== '/my-security' && <RecoveryCodesNudge />}
              {staffAccess.levelForPath(location.pathname) === 'view' && <ViewOnlyBanner />}
              <Outlet />
            </AppMenuRouteGuard>
          ) : (
            <Navigate to="/staff-portal" replace />
          )}
        </main>
      </div>

      {/* In-app bug reporter — floating button + modal, on every admin page */}
      <IssueReporterRoot
        user={buildIssueUser(email)}
        tenantId="super-admin"
        tenantName="Super Admin"
        productId={product.id}
      />
    </div>
  );
}

export default Layout;
